import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlContainer, FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { ActivatedRoute } from '@angular/router';
import { Customer } from '@klickdata/core/customer';
import { FormHelper } from '@klickdata/core/form';
import { CustomValidatorService } from '@klickdata/core/form/src/validators/custom-validator.service';
import { Resource } from '@klickdata/core/resource';
import { OccasionDate } from '@klickdata/core/resource/src/resource.model';

import { takeUntil, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
@Component({
    selector: 'app-resource-access-and-availability',
    templateUrl: './resource-access-and-availability.component.html',
    styleUrls: ['./resource-access-and-availability.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class ResourceAccessAndAvailabilityComponent extends OnDestroyHandler implements OnInit, AfterViewInit {
    private _resource: Resource;
    @Input() resourceLabel: string;
    @Input() set resource(value: Resource) {
        this._resource = value;
        if (this._resource?.id) {
            this.updateForm();
        }
    }
    get resource(): Resource {
        return this._resource;
    }
    @Input() customer: Customer;
    @Output() onEditResourceAccessPermision: EventEmitter<{ permType: string; value: any }> = new EventEmitter<{
        permType: string;
        value: any;
    }>();
    @Output() onUpdateResRecurring: EventEmitter<number> = new EventEmitter<number>();
    public resourceForm: FormGroup;
    public overdueOccasions: FormArray;
    public unlimited_seats = new FormControl(true);
    @ViewChild('occasionsExpansionPanel') occasionsExpansionPanel: MatExpansionPanel;
    @ViewChild('occasionsExpansionPanel', { read: ElementRef }) occasionsExpansionPanelRef: ElementRef<HTMLElement>;

    constructor(
        protected parentFormDirective: FormGroupDirective,
        private fb: FormBuilder,
        protected validatorService: CustomValidatorService,
        protected route: ActivatedRoute
    ) {
        super();
        this.resourceForm = parentFormDirective.form;
        this.buildForm(this.resourceForm);
    }

    ngOnInit(): void {
        this.resourceOccasions.valueChanges.subscribe((dates) => {
            if (
                dates.some((date) => (date.application_start_date || date.application_end_date) && !this.resource?.id)
            ) {
                this.resourceForm.get('collaboration.enroll_approval_required.status').setValue(true);
            }
        });

        this.unlimited_seats.valueChanges.pipe(takeUntil(this.destroy)).subscribe((value) => {
            if (value) {
                this.totalSeats.patchValue(null);
                this.totalSeats.markAsDirty();
            }
        });
    }
    ngAfterViewInit(): void {
        const contextParam = this.route.snapshot.queryParamMap.get('context');
        if (contextParam === 'occasions' && this.occasionsExpansionPanel) {
            this.occasionsExpansionPanel.open();
            setTimeout(() => {
                this.occasionsExpansionPanelRef?.nativeElement?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }, 500);
        }
    }
    private buildForm(resForm: FormGroup) {
        resForm.addControl('always_available', new FormControl(true));
        resForm.addControl('start_date', new FormControl(''));
        resForm.addControl('end_date', new FormControl(''));
        resForm.addControl(
            'collaboration',
            this.fb.group({
                view: this.fb.group({
                    status: [false],
                    exclude: [false],
                    metadata: this.fb.group({
                        publisher_logo: [],
                        diploma_logos: this.fb.group({
                            logo_1: this.fb.group({
                                media_id: [null],
                                alignment: [null],
                            }),
                            logo_2: this.fb.group({
                                media_id: [null],
                                alignment: [null],
                            }),
                            logo_3: this.fb.group({
                                media_id: [null],
                                alignment: [null],
                            }),
                        }),
                    }),
                }),
                mandatory: this.fb.group({
                    status: [false],
                    exclude: [false],
                }),
                enroll_approval_required: this.fb.group({
                    status: [false],
                    exclude: [false],
                    metadata: this.fb.group(
                        {
                            occasion_dates: this.fb.array([]),
                            occasion_always_open: [true],
                            total_seats: [null],
                            // location: [],
                        },
                        { validators: [this.validatorService.occasionDatesValidator] }
                    ),
                }),
                signoff_required: this.fb.group({
                    status: [false],
                    exclude: [false],
                }),
            })
        );
    }
    private updateForm() {
        if (!!this.resource.customerOccasionDates?.length) {
            this.resourceOccasions.clear();
            this.resource.customerOccasionDates.forEach((occasion) => this.addDate(occasion));
            this.overdueOccasions = this.getOverdueOccasions();
            FormHelper.resetForm(this.resourceOccasions);
        }
        this.enrollmentAlwaysOpen.patchValue(
            this.resource.collaboration?.enroll_approval_required?.metadata?.occasion_always_open
        );
        FormHelper.resetForm(this.enrollmentAlwaysOpen);
        if (this.enrollmentAlwaysOpen.value) {
            this.resourceOccasions.clear();
        }

        this.unlimited_seats.patchValue(
            this.resource.collaboration?.enroll_approval_required?.metadata?.total_seats === null
        );
        this.totalSeats.patchValue(
            this.resource.collaboration?.enroll_approval_required?.metadata?.total_seats === null
                ? null
                : this.resource.collaboration?.enroll_approval_required?.metadata?.total_seats
        );

        FormHelper.resetForm(this.totalSeats);
    }
    addDate(occasionDate?: OccasionDate) {
        const formGroup = this.fb.group({
            id: [occasionDate ? occasionDate.id : null],
            start_date: [occasionDate ? occasionDate.start_date : ''],
            end_date: [occasionDate ? occasionDate.end_date : ''],
            application_start_date: [occasionDate ? occasionDate.application_start_date : ''],
            application_end_date: [occasionDate ? occasionDate.application_end_date : ''],
            total_seats: [occasionDate ? occasionDate.total_seats : 0],
            unlimited_seats: [!!occasionDate ? occasionDate?.total_seats == null : true],
            location: [occasionDate ? occasionDate.location : ''],
            grib: [occasionDate ? occasionDate.grib : ''],
            activity: [occasionDate ? occasionDate.activity : ''],
            other: [occasionDate ? occasionDate.other : ''],
            target_group: [occasionDate ? occasionDate.target_group : ''],
            expanded: [false],
        });
        if (occasionDate && !!occasionDate.disabled) {
            formGroup.disable();
        }
        this.resourceOccasions.push(formGroup);
        if (!occasionDate) {
            this.resourceOccasions.markAsDirty();
        }
    }

    removeDate(i: number) {
        const toRemove: number[] = [];
        this.resourceOccasions.controls.forEach((ctrl, idx) => {
            if (!ctrl.disabled) {
                toRemove.push(idx);
            }
        });
        toRemove.reverse().forEach((idx) => this.resourceOccasions.removeAt(idx));
    }
    get resourceOccasions(): FormArray {
        return this.resourceForm.get('collaboration.enroll_approval_required.metadata.occasion_dates') as FormArray;
    }
    get enrollmentAlwaysOpen(): FormControl {
        return this.resourceForm.get(
            'collaboration.enroll_approval_required.metadata.occasion_always_open'
        ) as FormControl;
    }
    get totalSeats(): FormControl {
        return this.resourceForm.get('collaboration.enroll_approval_required.metadata.total_seats') as FormControl;
    }
    getOverdueOccasions(): FormArray {
        const occasions = this.resourceForm.get(
            'collaboration.enroll_approval_required.metadata.occasion_dates'
        ) as FormArray;
        const overDue = new FormArray([]);
        occasions.controls.forEach((occasion) => {
            if (occasion.disabled) {
                overDue.push(occasion);
            }
        });
        return overDue;
    }
    editResourceAccessPermision(permType: string, value: any) {
        this.onEditResourceAccessPermision.emit({ permType, value });
    }
}
